import { getFilterData } from "../../../Api/Api";
import { getBatteryAlertIncidents } from "../../../Api/BmsFaultsApi";
import { getPlatformAlertIncidents, normalizeIncident } from "../../../Api/PlatformAlertsApi";
import {
  convertZonetoUTCStart,
  convertZonetoUTCEnd,
  getMetricTimeStamp,
} from "../../../Helper/DatePicker/DateConverter";
import { transformEventsTree, ANNOTATION_SOURCE } from "./CommonFunctions";

/**
 * Build the Events selector hierarchy for Advanced Analytics.
 *
 * Always includes the "bmsfaults" root (from diagnosticsMetaBOP.errorDescription,
 * keyed by orgErrorCode). When the org has Smart Alerts access, also includes a
 * "smartalerts" root (from alertMeta.alertDescription, keyed by alertCode).
 *
 * A root is only included when it has events, so the selector never shows an
 * empty group; falls back to the (possibly empty) bmsfaults root so an empty
 * state can still render. The Smart Alerts fetch fails soft — an error there
 * never breaks the bmsfaults root.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.hasSmartAlertsAccess]
 * @returns {Promise<object>} merged hierarchy keyed by root name
 */
export const buildEventsHierarchy = async ({ hasSmartAlertsAccess = false } = {}) => {
  const bmsFilterConfig = {
    type: "dynamic",
    queryParams: { table: "diagnosticsMetaBOP", column: "errorDescription", keyColumn: "orgErrorCode" },
  };

  const smartAlertsPromise = hasSmartAlertsAccess
    ? getFilterData({
        type: "dynamic",
        queryParams: { table: "alertMeta", column: "alertDescription", keyColumn: "alertCode" },
      }).catch((err) => {
        console.error("Error fetching smart alerts events:", err);
        return null;
      })
    : Promise.resolve(null);

  const [bmsRes, smartRes] = await Promise.all([getFilterData(bmsFilterConfig), smartAlertsPromise]);

  const bmsList = bmsRes?.response?.response?.filterData || [];
  const smartList = smartRes?.response?.response?.filterData || [];

  const bmsTree = transformEventsTree(bmsList, "bmsfaults");
  const smartTree = transformEventsTree(smartList, "smartalerts");

  const mergedHierarchy = {};
  if (bmsList.length > 0) Object.assign(mergedHierarchy, bmsTree);
  if (smartList.length > 0) Object.assign(mergedHierarchy, smartTree);
  if (Object.keys(mergedHierarchy).length === 0) Object.assign(mergedHierarchy, bmsTree);

  return mergedHierarchy;
};

/**
 * Fetch chart annotations for the selected events. Events can span two roots
 * ("bmsfaults", "smartalerts"); each source is fetched from its own endpoint,
 * normalized to the shared annotation shape, tagged with `source` and
 * concatenated into a single annotation array. Timestamps are converted to the
 * user timezone. A failure in one source does not drop the other.
 *
 * @param {Array} events      — selected events (each has `metric` code + `raw` path)
 * @param {string} dateValue  — selected date (user tz), used for the trigger/resolve window
 * @param {string} batteryID
 * @returns {Promise<Array>} combined, source-tagged annotations
 */
export const fetchEventAnnotations = async (events, dateValue, batteryID) => {
  if (!events || events.length === 0) return [];

  const startDateUTC = convertZonetoUTCStart(dateValue);
  const endDateUTC = convertZonetoUTCEnd(dateValue);
  const triggerOrResolved = JSON.stringify({ startDateUTC, endDateUTC });

  // Smart-alert events carry a "smartalerts." raw path; everything else
  // (including legacy events without a raw path) is treated as BMS faults.
  const smartEvents = events.filter((e) => e.raw?.startsWith("smartalerts."));
  const bmsEvents = events.filter((e) => !e.raw?.startsWith("smartalerts."));

  const convertTimes = (alert) => ({
    ...alert,
    customerDBTimeInsert: alert.customerDBTimeInsert
      ? getMetricTimeStamp(alert.customerDBTimeInsert, "YYYY-MM-DD HH:mm:ss")
      : alert.customerDBTimeInsert,
    resolveCustomerDBTime: alert.resolveCustomerDBTime
      ? getMetricTimeStamp(alert.resolveCustomerDBTime, "YYYY-MM-DD HH:mm:ss")
      : alert.resolveCustomerDBTime,
  });

  const tasks = [];

  if (bmsEvents.length > 0) {
    tasks.push(
      getBatteryAlertIncidents({}, batteryID, {
        orgErrorCode: bmsEvents.map((event) => event.metric),
        triggerOrResolved,
      })
        .then((res) => (res?.response?.data || []).map((alert) => ({
          ...convertTimes(alert),
          source: ANNOTATION_SOURCE.BMS_FAULT,
        })))
        .catch((error) => {
          console.error("Error fetching BMS fault annotations:", error);
          return [];
        })
    );
  }

  if (smartEvents.length > 0) {
    tasks.push(
      getPlatformAlertIncidents({}, batteryID, {
        // Filter by alertDescription (event.label), NOT alertCode. The Events
        // selector groups alerts by description, so one dropdown entry can map
        // to several alertCodes; filtering by the single surviving code would
        // miss incidents raised under the others. Description matches what the
        // user selected and catches them all.
        alertDescription: smartEvents.map((event) => event.label),
        triggerOrResolved,
      })
        .then((res) => (res?.response?.data || []).map((alert) => ({
          // normalizeIncident maps smart-alert fields onto the same field names
          // the annotation pipeline consumes.
          ...convertTimes(normalizeIncident(alert)),
          source: ANNOTATION_SOURCE.SMART_ALERT,
        })))
        .catch((error) => {
          console.error("Error fetching smart alert annotations:", error);
          return [];
        })
    );
  }

  const results = await Promise.all(tasks);
  return results.flat();
};
